import * as signalR from "@microsoft/signalr";
import { HubConnection } from "@microsoft/signalr";
import React from "react";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "~/config";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import useAuth from "~/utils/hooks/useAuth";
import { useReconnect } from "./useReconnect";
import { useWatchConnectionState } from "./useWatchConnectionState";

const reconnectTime = 10 * 1000; // 10s
export type UseSignalRReturn = {
  connection: HubConnection;
  connected: boolean;
  initialLoading: boolean;
};
export function useSignalR(): UseSignalRReturn {
  const [connected, setConnected] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const errorHandler = useApiErrorHandler();
  const { t } = useTranslation("signalr");
  const { getToken } = useAuth();

  const connectionRef = React.useRef(
    new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/inSeaHub`, {
        accessTokenFactory: getToken as () => Promise<string>,
      })
      .build(),
  );

  const connect = React.useCallback(() => {
    if (connectionRef.current.state !== signalR.HubConnectionState.Disconnected)
      return;

    connectionRef.current
      .start()
      .then(() => setConnected(true))

      .catch(() => {
        errorHandler(new Error(t("connection-start-error")));
      })
      .finally(() => setInitialLoading(false));
  }, [errorHandler, t]);

  // Connect to hub
  React.useEffect(() => {
    connect();
  }, [connect]);

  useReconnect({
    connected,
    connection: connectionRef.current,
    onConnect: React.useCallback(() => {
      setConnected(true);
    }, []),
    reconnectTime,
  });

  useWatchConnectionState({
    connection: connectionRef.current,
    onConnectionClose: React.useCallback(
      (err) => {
        if (err) errorHandler(new Error(t("connection-closed")));
        setConnected(false);
      },
      [errorHandler, t],
    ),
    onReconnect: React.useCallback(() => {
      setConnected(true);
    }, []),
  });

  return {
    connected,
    initialLoading,
    connection: connectionRef.current,
  };
}
